import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { BrutalInput } from "@/components/ui/BrutalInput";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { sanitizeName } from "@/lib/sanitize";

interface PayoutModalProps {
  balance: number;
  onClose: () => void;
  onSuccess: () => void;
}

export const PayoutModal = ({ balance, onClose, onSuccess }: PayoutModalProps) => {
  const { t } = useTranslation();
  const [amount, setAmount] = useState<string>(balance.toString());
  const [accountHolder, setAccountHolder] = useState("");
  const [iban, setIban] = useState("");
  const [bicSwift, setBicSwift] = useState("");
  const [fiscalCode, setFiscalCode] = useState("");
  const [loading, setLoading] = useState(false);

  // Basic IBAN validation (starts with 2 letters, followed by digits and alphanumerics)
  const isValidIban = (value: string) => {
    const ibanRegex = /^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/;
    return ibanRegex.test(value.replace(/\s/g, '').toUpperCase());
  };

  // Basic Italian fiscal code validation
  const isValidFiscalCode = (value: string) => {
    const cfRegex = /^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]$/i;
    return cfRegex.test(value.replace(/\s/g, ''));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const payoutAmount = parseFloat(amount);
    if (payoutAmount < 50 || payoutAmount > balance) {
      toast({ title: t("earnings.payout.invalidAmount"), variant: "error" });
      return;
    }

    if (!accountHolder.trim()) {
      toast({ title: t("earnings.payout.accountHolderRequired"), variant: "error" });
      return;
    }

    if (!isValidIban(iban)) {
      toast({ title: t("earnings.payout.invalidIban"), variant: "error" });
      return;
    }

    if (!isValidFiscalCode(fiscalCode)) {
      toast({ title: t("earnings.payout.invalidFiscalCode"), variant: "error" });
      return;
    }

    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const bankDetails = {
        account_holder: sanitizeName(accountHolder.trim()),
        iban: iban.replace(/\s/g, '').toUpperCase(),
        bic_swift: bicSwift.trim() || null,
        fiscal_code: fiscalCode.trim().toUpperCase()
      };

      const { error } = await supabase
        .from("payouts")
        .insert({
          user_id: user.id,
          payout_type: 'bank',
          amount: payoutAmount,
          bank_details: bankDetails,
          status: 'pending'
        });

      if (error) throw error;

      toast({ title: t("earnings.payout.requestSuccess"), variant: "success" });
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error("Payout request error:", error);
      toast({ title: error.message, variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md border-[3px] border-dark shadow-[8px_8px_0px_0px] shadow-dark">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">
            {t("earnings.payout.title")}
          </DialogTitle>
          <DialogDescription>
            {t("earnings.payout.bankTransferDescription")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Amount */}
          <div>
            <label className="font-bold mb-2 block">{t("earnings.payout.amount")}</label>
            <BrutalInput
              type="number"
              min={50}
              max={balance}
              step={0.01}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
            />
            <p className="text-sm text-muted-foreground mt-1">
              {t("earnings.payout.minAmount")}: €50 | {t("earnings.payout.available")}: €{balance.toFixed(2)}
            </p>
          </div>

          {/* Account Holder */}
          <div>
            <label className="font-bold mb-2 block">{t("earnings.payout.accountHolder")}</label>
            <BrutalInput
              type="text"
              value={accountHolder}
              onChange={(e) => setAccountHolder(e.target.value)}
              placeholder="Mario Rossi"
              required
            />
            <p className="text-xs text-muted-foreground mt-1">
              {t("earnings.payout.accountHolderHint")}
            </p>
          </div>

          {/* IBAN */}
          <div>
            <label className="font-bold mb-2 block">{t("earnings.payout.iban")}</label>
            <BrutalInput
              type="text"
              value={iban}
              onChange={(e) => setIban(e.target.value)}
              placeholder="IT60X0542811101000000123456"
              required
            />
            <p className="text-xs text-muted-foreground mt-1">
              {t("earnings.payout.ibanHint")}
            </p>
          </div>

          {/* BIC/SWIFT */}
          <div>
            <label className="font-bold mb-2 block">
              {t("earnings.payout.bicSwift")} <span className="font-normal text-muted-foreground">({t("common.optional")})</span>
            </label>
            <BrutalInput
              type="text"
              value={bicSwift}
              onChange={(e) => setBicSwift(e.target.value)}
              placeholder="BCITITMM"
            />
          </div>

          {/* Fiscal Code */}
          <div>
            <label className="font-bold mb-2 block">{t("earnings.payout.fiscalCode")}</label>
            <BrutalInput
              type="text"
              value={fiscalCode}
              onChange={(e) => setFiscalCode(e.target.value)}
              placeholder="RSSMRA85M01H501Z"
              required
            />
            <p className="text-xs text-muted-foreground mt-1">
              {t("earnings.payout.fiscalCodeHint")}
            </p>
          </div>

          {/* Info Box */}
          <div className="bg-warning/10 border-[2px] border-warning p-4 rounded">
            <p className="text-sm font-bold mb-1">⏱️ {t("earnings.payout.processingTime")}</p>
            <p className="text-xs text-muted-foreground">
              {t("earnings.payout.processingTimeDesc")}
            </p>
          </div>

          <div className="flex gap-3 pt-4">
            <BrutalButton type="button" variant="outline" onClick={onClose} className="flex-1">
              {t("common.cancel")}
            </BrutalButton>
            <BrutalButton type="submit" disabled={loading} className="flex-1">
              {loading ? t("common.loading") : t("earnings.payout.submit")}
            </BrutalButton>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
